import Fetcher, {
  FetcherBackendResposeError,
  FetcherRequestOptions,
  FetcherResponse,
  FetcherResponseErrorType,
  isResponseError,
  FetcherResponseError,
} from 'javascript/lander/utils/fetcher'
import { StripeSessionData, SubmitPayload, SubmitResponse } from './types'
import { renderedHref } from '../Utils/checkout-path'
import { fetchCheckoutNonce, checkoutNonceParams } from 'javascript/lander/nonce'
import { StripeError } from '@stripe/stripe-js'
import { normalizeServerCheckoutError } from '../Services/checkout-error-classification'

export function makeApiFetcher<T>(options: { headers?: HeadersInit } & FetcherRequestOptions): Fetcher<T> {
  const { headers, ...requestOptions } = options
  const fetcher = new Fetcher<T>({
    debug: true,
    pathPrefix: '/user_pages/api/v1/',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(headers ?? {}),
    },
    requestOptions: {
      retries: 3,
      timeoutMS: 30000,
      timeoutAfterRetrial: 1000,
      shouldCaptureServerError: true,
      convertThrowToErrorResponse: true,
      ...requestOptions,
    },
  })

  return fetcher
}

export const stripeSessionApiFetcher = makeApiFetcher<StripeSessionData>({
  onlyOneInflightRequest: true,
  simulateNetworkError: false,
  // `stripe_get_session` only syncs the contact and mints a customer session, so a
  // replay is harmless. It is also the call that fails when Stripe's per-object lock
  // is held (ENG-7225) — the one case where retrying a 5xx is exactly the fix.
  retryServerErrors: true,
  // Same reasoning for a timed-out attempt. This is deliberately not switched on for
  // `stripeSubmitFetcher` below: replaying a submit is a much heavier claim to make.
  retryTimeouts: true,
  // ...but bound the sequence in wall clock. Making `retries` finally apply to timeouts is
  // a behaviour change, and on the inherited `timeoutMS: 30000` / `retries: 3` it would
  // cost the buyer 3 x 30s + 2 x 1s = 92s of blank checkout before the banner, and issue 3
  // backend requests per browser. Aborting a fetch does not stop the Rails request — Puma
  // holds the thread until it responds — so those retries would amplify load onto a pool
  // whose threads are already parked on the object-lock sleep, which is precisely when a
  // timeout is most likely. 15s clears the server's 8s sleep budget plus a normal Stripe
  // round trip, and 2 attempts keep the retry that actually fixes a transient lock while
  // holding the worst case at ~31s — the same ceiling buyers saw before.
  timeoutMS: 15000,
  retries: 2,
})

export const stripeSubmitFetcher = new Fetcher<SubmitResponse>({
  debug: true,
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-CF2-POST-TYPE': 'submit',
  },
  requestOptions: {
    retries: 3,
    timeoutMS: 60000,
    timeoutAfterRetrial: 1000,
    shouldCaptureServerError: true,
    convertThrowToErrorResponse: true,
    onlyOneInflightRequest: true,
    canAbort: false,
  },
})

export function isStripeError(error: any): error is StripeError {
  if (typeof error === 'object') {
    const stripeError = error as StripeError
    if (stripeError?.code && stripeError?.message) {
      return true
    }
  }

  return false
}

export async function postSubmit(payload: SubmitPayload): Promise<FetcherResponse<SubmitResponse>> {
  // Single Fetcher call: it owns retries and the onlyOneInflightRequest
  // double-submit guard, which an external retry loop would defeat. The
  // nonce is minted once, so a Fetcher network-retry reuses it — the nonce
  // is always enforced, so that retry 422s and the buyer refreshes (the
  // nonce is single-use). Acceptable vs. the double-charge risk of looping.
  const nonce = await fetchCheckoutNonce()
  const response = await stripeSubmitFetcher.fetch(
    renderedHref(),
    {
      credentials: 'same-origin',
      body: JSON.stringify({ ...payload, ...checkoutNonceParams(nonce) }),
    },
    {}
  )

  if (isResponseError(response)) {
    return response
  } else {
    let body
    if (response.headers.get('Content-Type').includes('text/html')) {
      body = {
        next_url: response.headers.get('Location'),
      }
    } else {
      body = await response.json()
    }
    if (response.ok) {
      return {
        ...body,
        headers: {
          get: (name: string) => response.headers?.get(name),
        },
      } as FetcherResponse<SubmitResponse>
    } else if (response.status === 422) {
      // NOTE: Would be better is the backend followed the same data structure
      // as the stripe errors so we wouldn't have to translate anythin in here.
      // I am doing this transformation to simplify the code paths when handling
      // responses afterwards.
      // Shared with the classifier so the two cannot drift: it keeps `code` (Stripe sends it as a
      // bare hash, Rebilly inside an array) and preserves `error_details`, which is what marks
      // this as our own rejection envelope rather than a payment-layer failure.
      return {
        fetcherErrorType: FetcherResponseErrorType.Backend,
        error: normalizeServerCheckoutError(body),
      } as FetcherBackendResposeError
    } else {
      // Anything else non-2xx that the fetcher did not already turn into an error -- an edge or
      // WAF response, say. Returning undefined here made handleServerResponse throw, and on the
      // one-click path that throw escaped the click handler and left the modal on its spinner.
      // A string `error` is refused as buyer copy, so this lands on the generic message.
      return {
        fetcherErrorType: FetcherResponseErrorType.Internal,
        error: `Unexpected response status ${response.status}`,
      } as FetcherResponseError
    }
  }
}

